import React, { useState } from 'react';
import { Modal } from '../common/Modal';
import { Guide } from '../../types/guide';
import { Organizer } from '../../types/organizer';
import { BanType } from '../../types/ban';
import { useBansStore } from '../../stores/bansStore';
import { useLanguage } from '../../contexts/LanguageContext'; // Import useLanguage
import toast from 'react-hot-toast';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  guides: Record<string, Guide>;
  organizers: Record<string, Organizer>;
};

export const AddBanModal = ({ isOpen, onClose, guides, organizers }: Props) => {
  const { translations } = useLanguage(); // Use translations
  const [selectedUserType, setSelectedUserType] = useState<'guide' | 'organizer'>('guide');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedBanType, setSelectedBanType] = useState<BanType>('full');
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState<'temporary' | 'permanent'>('temporary');
  const [expiryDate, setExpiryDate] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { createBan } = useBansStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedUserId || !reason) {
      toast.error(translations?.addBanModal?.errors?.allFieldsRequired || 'All fields are required');
      return;
    }

    if (duration === 'temporary' && !expiryDate) {
      toast.error(translations?.addBanModal?.errors?.expiryDateRequired || 'Please select an expiry date');
      return;
    }

    setIsLoading(true);
    try {
      const user = selectedUserType === 'guide'
        ? guides[selectedUserId]
        : organizers[selectedUserId];

      await createBan({
        userId: selectedUserId,
        userType: selectedUserType,
        userName: user.name,
        banType: selectedBanType,
        reason,
        createdAt: Date.now(),
        expiresAt: duration === 'permanent' ? null : new Date(expiryDate).getTime(),
        createdBy: localStorage.getItem('userId') || '',
        createdByName: localStorage.getItem('userName') || '',
        isActive: true
      });

      toast.success(translations?.addBanModal?.success?.banAdded || 'Ban added successfully');
      onClose();
    } catch (error) {
      toast.error(translations?.addBanModal?.errors?.banAddFailed || 'Failed to add ban');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={translations?.addBanModal?.title || 'Add New Ban'}>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* User Type Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {translations?.addBanModal?.labels?.userType || 'User Type'}
          </label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={selectedUserType === 'guide'}
                onChange={() => setSelectedUserType('guide')}
                className="text-primary-500 focus:ring-primary-500"
              />
              <span>{translations?.addBanModal?.labels?.guide || 'Guide'}</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={selectedUserType === 'organizer'}
                onChange={() => setSelectedUserType('organizer')}
                className="text-primary-500 focus:ring-primary-500"
              />
              <span>{translations?.addBanModal?.labels?.organizer || 'Organizer'}</span>
            </label>
          </div>
        </div>

        {/* User Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {selectedUserType === 'guide'
              ? translations?.addBanModal?.labels?.guide || 'Guide'
              : translations?.addBanModal?.labels?.organizer || 'Organizer'}
          </label>
          <select
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500"
            required
          >
            <option value="">
              {translations?.addBanModal?.labels?.selectUser || 'Select'} {selectedUserType === 'guide'
                ? translations?.addBanModal?.labels?.guide || 'Guide'
                : translations?.addBanModal?.labels?.organizer || 'Organizer'}
            </option>
            {Object.values(selectedUserType === 'guide' ? guides : organizers).map((user) => (
              <option key={user.id} value={user.id}>{user.name}</option>
            ))}
          </select>
        </div>

        {/* Ban Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {translations?.addBanModal?.labels?.banType || 'Ban Type'}
          </label>
          <select
            value={selectedBanType}
            onChange={(e) => setSelectedBanType(e.target.value as BanType)}
            className="w-full px-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500"
            required
          >
            <option value="full">{translations?.addBanModal?.banTypes?.full || 'Full Ban'}</option>
            <option value="accommodation">{translations?.addBanModal?.banTypes?.accommodation || 'Accommodation Ban'}</option>
            <option value="linking">{translations?.addBanModal?.banTypes?.linking || 'Linking Ban'}</option>
            <option value="camp">{translations?.addBanModal?.banTypes?.camp || 'Camp Ban'}</option>
          </select>
        </div>

        {/* Reason */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {translations?.addBanModal?.labels?.reason || 'Reason'}
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500"
            rows={3}
            required
          />
        </div>

        {/* Duration */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {translations?.addBanModal?.labels?.duration || 'Duration'}
          </label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={duration === 'temporary'}
                onChange={() => setDuration('temporary')}
                className="text-primary-500 focus:ring-primary-500"
              />
              <span>{translations?.addBanModal?.labels?.temporary || 'Temporary'}</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={duration === 'permanent'}
                onChange={() => setDuration('permanent')}
                className="text-primary-500 focus:ring-primary-500"
              />
              <span>{translations?.addBanModal?.labels?.permanent || 'Permanent'}</span>
            </label>
          </div>
        </div>

        {/* Expiry Date */}
        {duration === 'temporary' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {translations?.addBanModal?.labels?.expiryDate || 'Expiry Date'}
            </label>
            <input
              type="date"
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500"
              min={new Date().toISOString().split('T')[0]}
              required
            />
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            {translations?.addBanModal?.buttons?.cancel || 'Cancel'}
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className={`px-6 py-2 rounded-lg text-white transition-colors ${
              isLoading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-red-500 hover:bg-red-600'
            }`}
          >
            {isLoading
              ? translations?.addBanModal?.buttons?.adding || 'Adding...'
              : translations?.addBanModal?.buttons?.addBan || 'Add Ban'}
          </button>
        </div>
      </form>
    </Modal>
  );
};